import { Box3, Mesh, Object3D, Vector3 as THREE_Vector3 } from 'three';
import DomainModel from '../DomainModel';
import { Material as Material } from '../domain';
import { Vector3 } from '../math/vector3';
import { OrderedEffects } from '../utils';
import mfac from '../MaterialFactory';
import logging from '../logging';
import Application from '../Application';

const log = logging('view');

export interface DomainModel3D extends DomainModel {
    position: Vector3;
    rotation: Vector3;
    // TODO: make the scale optional
    scale: Vector3;
};

export default abstract class BaseView extends Object3D {

    model: DomainModel3D;
    highlights: Set<Mesh> = new Set();
    private _app: Application | undefined;

    protected updates: OrderedEffects = new OrderedEffects();

    constructor(model: DomainModel3D, app: Application) {
        super();

        this.model = model;
        this._app = app;
    }

    /**
     * Every implementing class should ensure that the init() method runs at
     * the end of the constructor or at least some time before the view is
     * needed in the scene.
     */
    init() {
        this.make();
        this.bind();
        this.bindTransform();
    }

    /**
     * Get the application from which this view was constructed. I don't
     * currently think we'll ever need to change this, so it's read-only.
     */
    get app(): Application {
        return this._app;
    }

    /**
     * Implementations should create an empty skeleton of their 3D children.
     */
    protected abstract make(): void;

    /**
     * Implementations should bind their model's change events to their
     * particular properties.
     */
    protected abstract bind(): void;

    /**
     * An automatic side-effect of the constructor is that this Object3D
     * instance (and its subclasses) will automatically track the 3D transform
     * of their corresponding models.
     */
    protected bindTransform(): void {
        this.model.position.bind('x',
                      () => this.position.x = this.model.position.x);
        this.model.position.bind('y',
                      () => this.position.y = this.model.position.y);
        this.model.position.bind('z',
                      () => this.position.z = this.model.position.z);


        this.model.rotation.bind('x',
                      () => this.rotation.x = this.model.rotation.x);
        this.model.rotation.bind('y',
                      () => this.rotation.y = this.model.rotation.y);
        this.model.rotation.bind('z',
                      () => this.rotation.z = this.model.rotation.z);


        this.model.scale.bind('x',
                      () => this.scale.x = this.model.scale.x);
        this.model.scale.bind('y',
                      () => this.scale.y = this.model.scale.y);
        this.model.scale.bind('z',
                      () => this.scale.z = this.model.scale.z);
    }

    protected bindMaterial(mesh: Mesh, material: Material) {
        material.bind('change', () => {
            mesh.material = mfac.makefrom(material);
            Application.do('pipeline-update');
        });

        if('opacity' in this.model) {
            this.model.bind('opacity', () => {
                const opacity = this.model.get('opacity') as number;
                mesh.material = mfac.makeTransparent(material, opacity);
                Application.do('pipeline-update');
            });
        }
    }

    /**
     * When a Mesh instance's property needs to be an array of materials, the
     * plain bindMaterial method won't work. This method takes care of that
     * case.
     */
    protected bindMaterialArray(mesh: Mesh, materials: Material[]) {
        mesh.material = new Array(materials.length);
        for(let i = 0; i < materials.length; i++) {
            materials[i].bind("change", () => {
                mesh.material[i] = mfac.makefrom(materials[i]);
            });
            if('opacity' in this.model) {
                this.model.bind('opacity', () => {
                    const opacity = this.model.get('opacity') as number;
                    mesh.material[i] = mfac.makeTransparent(materials[i], opacity);
                });
            }
        }
    }

    /**
     * If we are embedded in an Application instance, this requests that the
     * renderer render the next frame. Call this method after completing a
     * geometry update if you're not seeing your updates but they do appear
     * after a refresh, i.e. when the pipeline has paused rendering before
     * showing your change.
     *
     * Usually this is not required in a view because tool implementations
     * should already be updating the rendered view on user input.
     *
     * This is a little ugly because it leaks some coupling to the Application
     * from inside the views, but it's at least loose coupling.
     */
    protected pipelineUpdate() {
        if(this.app)
            this.app.pipeline.update = true;
    }

    /**
     * This has the wrong return signature to satisfy a quirk of TypeScript.
     * At the moment I don't need the clone to be completely the same as its
     * source, with its attachment to a model.
     */
    public clone(deep?: boolean): this {
        // In TypeScript, this.constructor is a Function, but we want it to be
        // a constructor. Not sure how to do it any better than this.
        let clone = new Object3D();
        clone.copy(this, deep);
        return clone as this;
    }

    /**
     * Computes the axis-aligned bounding box that precisely contains this
     * view.
     */
    public getVisualDimensons(): THREE_Vector3 {
        const box = new Box3();
        box.setFromObject(this, true);
        return box.getSize(new THREE_Vector3());
    }
}
